import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Save, Clock, Video, ArrowRight } from "lucide-react";

export default function PlanStep({ meeting, refresh, setActive, mid }) {
  const [title, setTitle] = useState(meeting.title || "");
  const [link, setLink] = useState(meeting.link || "");
  const [datetime, setDatetime] = useState(meeting.datetime || "");
  const [agenda, setAgenda] = useState(meeting.agenda?.length ? meeting.agenda : []);
  const [attendees, setAttendees] = useState(meeting.attendees?.length ? meeting.attendees : []);

  useEffect(() => { setAgenda(meeting.agenda || []); setAttendees(meeting.attendees || []); }, [meeting]);

  const platform = link.includes("teams") ? "Microsoft Teams" : link.includes("zoom") ? "Zoom" : link.includes("meet.google") ? "Google Meet" : link ? "Meeting link" : "";

  const addAgenda = () => setAgenda([...agenda, { title: "", minutes: 15, owner: "" }]);
  const updAgenda = (i, f, v) => { const a = [...agenda]; a[i] = { ...a[i], [f]: v }; setAgenda(a); };
  const rmAgenda = (i) => setAgenda(agenda.filter((_, x) => x !== i));
  const addAtt = () => setAttendees([...attendees, { name: "", role: "", org: "", topics: "" }]);
  const updAtt = (i, f, v) => { const a = [...attendees]; a[i] = { ...a[i], [f]: v }; setAttendees(a); };
  const rmAtt = (i) => setAttendees(attendees.filter((_, x) => x !== i));

  const save = async (advance) => {
    try {
      await api.put(`/meetings/${mid}`, { title, link, datetime, timezone: meeting.timezone || "Asia/Kolkata", agenda, attendees });
      toast.success("Meeting plan saved");
      refresh();
      if (advance) setActive("prepare");
    } catch { toast.error("Could not save"); }
  };

  const totalMin = agenda.reduce((s, a) => s + (Number(a.minutes) || 0), 0);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Meeting title</Label><Input data-testid="meeting-title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><Label>Date & time</Label><Input data-testid="meeting-datetime" type="datetime-local" value={datetime || ""} onChange={(e) => setDatetime(e.target.value)} /></div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-2"><Video size={14} /> Meeting link {platform && <span className="text-xs text-[#29abe2] font-medium">· {platform} detected</span>}</Label>
            <Input data-testid="meeting-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://teams.microsoft.com/… or Zoom / Google Meet" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2]"><Clock size={18} /> Agenda <span className="text-sm text-muted-foreground font-normal">· {totalMin} min total</span></div>
          <Button size="sm" variant="outline" onClick={addAgenda} data-testid="add-agenda" className="gap-1"><Plus size={14} /> Item</Button>
        </div>
        {agenda.length === 0 && <p className="text-sm text-muted-foreground">No agenda yet — add your first item or paste topics to discuss.</p>}
        <div className="space-y-2">
          {agenda.map((a, i) => (
            <div key={i} className="grid grid-cols-[2rem_1fr_6rem_1fr_auto] gap-2 items-center">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-[#29abe2]/15 text-[#1a7bb0] dark:text-[#5fc3f0] text-xs font-semibold">{i + 1}</span>
              <Input placeholder="Topic (e.g. Order-to-Cash process)" value={a.title} onChange={(e) => updAgenda(i, "title", e.target.value)} data-testid={`agenda-title-${i}`} />
              <Input type="number" placeholder="min" value={a.minutes} onChange={(e) => updAgenda(i, "minutes", e.target.value)} />
              <Input placeholder="Owner" value={a.owner} onChange={(e) => updAgenda(i, "owner", e.target.value)} />
              <Button variant="ghost" size="icon" className="text-red-500" onClick={() => rmAgenda(i)}><Trash2 size={15} /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-[#0f2557] dark:text-[#29abe2]">Attendees</div>
          <Button size="sm" variant="outline" onClick={addAtt} data-testid="add-attendee" className="gap-1"><Plus size={14} /> Attendee</Button>
        </div>
        <div className="space-y-2">
          {attendees.map((a, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
              <Input placeholder="Name" value={a.name} onChange={(e) => updAtt(i, "name", e.target.value)} data-testid={`attendee-name-${i}`} />
              <Input placeholder="Role" value={a.role} onChange={(e) => updAtt(i, "role", e.target.value)} />
              <Input placeholder="Organization" value={a.org} onChange={(e) => updAtt(i, "org", e.target.value)} />
              <Input placeholder="Expected topics" value={a.topics} onChange={(e) => updAtt(i, "topics", e.target.value)} />
              <Button variant="ghost" size="icon" className="text-red-500" onClick={() => rmAtt(i)}><Trash2 size={15} /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => save(false)} className="gap-2" data-testid="save-plan"><Save size={16} /> Save</Button>
        <Button onClick={() => save(true)} className="gap-2" data-testid="save-plan-next">Save & Prepare <ArrowRight size={16} /></Button>
      </div>
    </div>
  );
}
